import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Divider, Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiAlertTriangle, FiDatabase, FiMonitor, FiBell } from 'react-icons/fi';

const API_BASE = '/api/v1';

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setHealth(data); setLastChecked(new Date()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusColor = (status) => {
    switch (status) {
      case 'healthy': case 'connected': case 'complete': return 'green';
      case 'degraded': case 'incomplete': return 'orange';
      case 'not_configured': return 'yellow';
      case 'error': case 'unhealthy': return 'red';
      default: return 'gray';
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="green.500" /></Box>;
  if (!health) return <Alert status="error"><AlertIcon />Failed to load health check data</Alert>;

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Check</Heading>
          <Text color="gray.500">
            Service connectivity and status
            {lastChecked && ` — last checked ${lastChecked.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="green" onClick={fetchHealth} isLoading={loading}>Re-check</Button>
      </HStack>

      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : 'error'}
        borderRadius="lg" mb={6} py={4}
      >
        <AlertIcon boxSize={6} />
        <VStack align="start" spacing={0} ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={2}>{health.overall}</Badge>
          </Text>
        </VStack>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={6} color="blue.500" />
            <Heading size="md">Database</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm">{checks.database?.status || 'unknown'}</Badge>
            </HStack>
            {checks.database?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.database.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Monitors */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiMonitor} boxSize={6} color="green.500" />
            <Heading size="md">Monitors</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.monitors?.status)} fontSize="sm">{checks.monitors?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total</Text>
              <Text fontWeight="bold">{checks.monitors?.total || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Active</Text>
              <Text fontWeight="bold">{checks.monitors?.active || 0}</Text>
            </HStack>
            {checks.monitors?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.monitors.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Notifications */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiBell} boxSize={6} color="orange.500" />
            <Heading size="md">Notifications</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.notifications?.status)} fontSize="sm">{checks.notifications?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total Emails Sent</Text>
              <Text fontWeight="bold">{checks.notifications?.email_count || 0}</Text>
            </HStack>
            {checks.notifications?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.notifications.message}</Alert>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HealthCheck;
